<template>
  <div class="customer-satisfaction-page settings--content">
    <div class="medium-10 csat-settings-content">
      <label class="columns settings-item enable-csat-survey">
        {{ $t('INBOX_MGMT.CSAT.ENABLE_LABEL') }}
        <WootSwitch v-model="csatSurveyEnabled" />
      </label>
      <p>
        {{ $t('INBOX_MGMT.CSAT.ENABLE_DESCRIPTION') }}
      </p>

      <template v-if="csatSurveyEnabled">
        <div class="settings-item">
          <label class="columns">
            {{ $t('INBOX_MGMT.CSAT.DISPLAY_TYPE.LABEL') }}
          </label>
          <CSATDisplayTypeSelector
            :selected-type="displayType"
            @update="onDisplayTypeUpdate"
          />
        </div>

        <label class="columns settings-item">
          {{ $t('INBOX_MGMT.CSAT.MESSAGE.LABEL') }}
          <woot-message-editor
            v-model="message"
            class="message-editor"
            rows="3"
            :placeholder="$t('INBOX_MGMT.CSAT.MESSAGE.PLACEHOLDER')"
          />
        </label>

        <div class="settings-item">
          <label class="columns">
            {{ $t('INBOX_MGMT.CSAT.SURVEY_RULE.LABEL') }}
          </label>
          <p class="help-text">
            {{ $t('INBOX_MGMT.CSAT.SURVEY_RULE.DESCRIPTION_PREFIX') }}
            <select v-model="surveyRuleOperator" class="survey-rule-operator">
              <option value="contains">
                {{ $t('INBOX_MGMT.CSAT.SURVEY_RULE.OPERATOR.CONTAINS') }}
              </option>
              <option value="does_not_contain">
                {{
                  $t('INBOX_MGMT.CSAT.SURVEY_RULE.OPERATOR.DOES_NOT_CONTAINS')
                }}
              </option>
            </select>
            {{ $t('INBOX_MGMT.CSAT.SURVEY_RULE.DESCRIPTION_SUFFIX') }}
          </p>
          <multiselect
            v-model="selectedLabelValues"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :hide-selected="true"
            :options="labelOptions"
            :placeholder="
              $t('INBOX_MGMT.CSAT.SURVEY_RULE.SELECT_PLACEHOLDER')
            "
            selected-label
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
          />
        </div>
        <div class="settings-item">
          <label class="columns">
            {{ $t('INBOX_MGMT.CSAT.LIFETIME.LABEL') }}
          </label>
          <multiselect
            v-model="lifeTimeDay.selected"
            :options="lifeTimeDay.options"
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
            track-by="value"
            label="label"            
          />
        </div>
      </template>

      <woot-submit-button
        class="csat-submit-button"
        :button-text="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        :loading="uiFlags.isUpdating"
        @click="saveSettings"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import { CSAT_DISPLAY_TYPES } from 'shared/constants/messages';
import SettingsSection from '../../../../../components/SettingsSection';
import CSATDisplayTypeSelector from './components/CSATDisplayTypeSelector.vue';
import WootSwitch from 'components/ui/Switch';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';

export default {
  components: {
    SettingsSection,
    CSATDisplayTypeSelector,
    WootSwitch,
    WootMessageEditor,
  },
  mixins: [alertMixin],
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      csatSurveyEnabled: false,
      displayType: CSAT_DISPLAY_TYPES.EMOJI,
      message: '',
      surveyRuleOperator: 'contains',
      selectedLabelValues: [],
      lifeTimeDay: {
        selected: null,
        options: [1, 2, 3, 7, 14, 30, 45, 60, 90, 180, 364].map(d => {
          let label = `${d} ${this.$t('INBOX_MGMT.CSAT.LIFETIME.OPTIONS.DAY')}`;
          if(d > 1){
            label = `${d} ${this.$t('INBOX_MGMT.CSAT.LIFETIME.OPTIONS.DAYS')}`;
          }
          return {
            value: d,
            label: label
          }
        })
      }
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
      labels: 'labels/getLabels',
    }),
    labelOptions() {
      return this.labels.map(label => label.title);
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.$store.dispatch('labels/get');
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.csatSurveyEnabled = this.inbox.csat_survey_enabled || false;
      const csatConfig = this.inbox.csat_config || {};
      this.displayType = csatConfig.display_type || CSAT_DISPLAY_TYPES.EMOJI;
      this.message = csatConfig.message || '';
      const surveyRules = csatConfig.survey_rules || {};
      this.surveyRuleOperator = surveyRules.operator || 'contains';
      this.selectedLabelValues = surveyRules.values || [];
      this.lifeTimeDay.selected = this.lifeTimeDay.options.find((v) => v.value == (csatConfig.life_time_days ?? 1));
    },
    onDisplayTypeUpdate(value) {
      this.displayType = value;
    },
    async saveSettings() {
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          csat_survey_enabled: this.csatSurveyEnabled,
          csat_config: {
            display_type: this.displayType,
            message: this.message,
            survey_rules: {
              operator: this.surveyRuleOperator,
              values: this.selectedLabelValues,
            },
            life_time_days: parseInt(this.lifeTimeDay.selected.value)
          },
        });
        this.showAlert(this.$t('INBOX_MGMT.CSAT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(
          error.message || this.$t('INBOX_MGMT.CSAT.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<style scoped lang="scss">
.csat-settings-content {
  display: flex;
  flex-direction: column;
  outline: 1px var(--s-75) solid;
  outline-offset: -1px;
  border-radius: var(--border-radius-medium);
  padding: var(--space-normal);
}

.settings-item {
  padding-bottom: var(--space-normal);
}

.survey-rule-operator {
  display: inline-block;
  width: auto;
}

.csat-submit-button {
  margin-top: auto;
  align-self: start;
}

.enable-csat-survey {
  padding-bottom: 0;
  display: flex;
  justify-content: space-between;
}
</style>
